import { compileSerializedValue, printable, registeredReference, throwParseError, unset } from "@arktype/util";
import { BaseConstraint } from "../constraint.js";
import { Disjoint } from "../shared/disjoint.js";
import { intersectNodes } from "../shared/intersections.js";
export const intersectProps = (l, r, ctx) => {
    if (l.key !== r.key)
        return null;
    const key = l.key;
    let value = intersectNodes(l.value, r.value, ctx);
    const kind = l.required || r.required ? "required" : "optional";
    if (value instanceof Disjoint) {
        if (kind === "optional")
            value = ctx.$.keywords.never.raw;
        else
            return value.withPrefixKey(l.compiledKey);
    }
    if (kind === "required") {
        return ctx.$.node("required", {
            key,
            value
        });
    }
    const defaultIntersection = l.hasDefault() ?
        r.hasDefault() ?
            l.default === r.default ?
                l.default
                : throwParseError(`Invalid intersection of default values ${printable(l.default)} & ${printable(r.default)}`)
            : l.default
        : r.hasDefault() ? r.default
            : unset;
    return ctx.$.node("optional", {
        key,
        value,
        // unset is stripped during parsing
        default: defaultIntersection
    });
};
export class BaseProp extends BaseConstraint {
    required = this.kind === "required";
    impliedBasis = this.$.keywords.object.raw;
    serializedKey = compileSerializedValue(this.key);
    compiledKey = typeof this.key === "string" ? this.key : this.serializedKey;
    _transform(mapper, ctx) {
        ctx.path.push(this.key);
        const result = super._transform(mapper, ctx);
        ctx.path.pop();
        return result;
    }
    defaultValueMorphs = [
        data => {
            data[this.key] = this.default;
            return data;
        }
    ];
    defaultValueMorphsReference = registeredReference(this.defaultValueMorphs);
    hasDefault() {
        return "default" in this;
    }
    traverseAllows = (data, ctx) => {
        if (this.key in data) {
            // ctx will be undefined if this node isn't context-dependent
            ctx?.path.push(this.key);
            const allowed = this.value.traverseAllows(data[this.key], ctx);
            ctx?.path.pop();
            return allowed;
        }
        return !this.required;
    };
    traverseApply = (data, ctx) => {
        if (this.key in data) {
            ctx.path.push(this.key);
            this.value.traverseApply(data[this.key], ctx);
            ctx.path.pop();
        }
        else if (this.hasKind("required"))
            ctx.error(this.errorContext);
        else if (this.hasKind("optional") && this.hasDefault())
            ctx.queueMorphs(this.defaultValueMorphs);
    };
    compile(js) {
        js.if(`${this.serializedKey} in data`, () => js.traverseKey(this.serializedKey, `data${js.prop(this.key)}`, this.value));
        if (this.hasKind("required")) {
            js.else(() => {
                if (js.traversalKind === "Apply")
                    return js.line(`ctx.error(${this.compiledErrorContext})`);
                else
                    return js.return(false);
            });
        }
        else if (js.traversalKind === "Apply" && "default" in this) {
            js.else(() => js.line(`ctx.queueMorphs(${this.defaultValueMorphsReference})`));
        }
        if (js.traversalKind === "Allows")
            js.return(true);
    }
}
